import { useEffect, useRef } from "react";

/**
 * Reload the page after N minutes of true user inactivity.
 *
 * Activity sources: mousedown, keydown, touchstart, wheel, scroll, mousemove,
 * pointerdown. Pointer-based events cover desktop, touch, and stylus.
 *
 * Safety rails:
 *   - Only reloads when `document.visibilityState === "visible"`. If the tab
 *     is backgrounded, we skip the reload and reset the timer on next focus
 *     so we never nuke a cached state the user never interacted with.
 *   - Pauses the timer when the page is hidden (overnight phones with the
 *     tab backgrounded don't reload every 5 min).
 *   - Skips if the user is mid-form — a single input/textarea/contenteditable
 *     has focus → consider it active.
 */
export default function useIdleReload(minutes = 5) {
  const timerRef = useRef(null);

  useEffect(() => {
    const ms = minutes * 60 * 1000;
    if (!Number.isFinite(ms) || ms <= 0) return;

    const isEditingField = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (el.isContentEditable) return true;
      return false;
    };

    const armTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        if (document.visibilityState !== "visible") return; // will rearm on focus
        if (isEditingField()) {
          // User still typing — rearm without reloading.
          armTimer();
          return;
        }
        // Soft reload — picks up new SW bundle if one is waiting.
        window.location.reload();
      }, ms);
    };

    const onActivity = () => {
      armTimer();
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        armTimer();
      } else if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const events = [
      "mousedown", "keydown", "touchstart", "pointerdown",
      "wheel", "scroll", "mousemove",
    ];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    document.addEventListener("visibilitychange", onVisibility);

    armTimer();

    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener("visibilitychange", onVisibility);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [minutes]);
}
